import type { GenericObject, IPencilOfferInputsIncentive, OfferType, PaymentTerm, Terms } from '@makemydeal/dr-platform-types';
import { BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import * as types from '../types/actionTypes';
import { getOfferTypeTermId } from '../selectors/penciledInputsSelectors';
import { convertRatesFromSourceType, getOfferTypeTerms, mergeTerms } from '../utils';
import { keepLockedProgramOnUpqTerms } from '../utils/explicitProgramLockUtils';
import { isRegularGridRowAction } from '../utils/paymentGridUtils';
import { CALC_ENDPOINTS } from '../constants/state';
import { REQUEST_LEDGER_REBATES_META } from '../constants/payment';
import type { MergedTerms } from '../types/termsTypes';
import {
    buildConditionalIncentiveKey,
    buildNonConditionalIncentiveKey,
    getOverriddenIncentiveCodes,
    isIncentiveChangeActionType,
    isManualIncentiveByProvenance,
    isStoredDealerCash,
    isSupersededTermPaymentResponse,
    parseIncentiveId,
    sumLedgerRebates,
    updateTermIncentives
} from '../utils/incentives';
import { initialState } from './initialState';
import { shouldRevertPaymentOnFailure } from './paymentRevertUtils';
import type { AnyAction } from 'redux';
import type { LedgerAwareManualIncentives, PenciledInputsState } from '../types/commonTypes';

const parsePrefixedIds = (ids: string[]): number[] => ids.map(parseIncentiveId).filter((id) => !Number.isNaN(id));

const getTermConditionalIds = (term: GenericObject): number[] =>
    parsePrefixedIds((term.incentives?.conditionals ?? []).map((incentive: { id: string }) => incentive.id));

const getCatalogNonConditionalIds = (term: GenericObject): number[] =>
    parsePrefixedIds((term.incentives?.nonConditionals ?? []).map((incentive: { id: string }) => incentive.id));

const getTermCompatibleIds = (term: GenericObject): number[] =>
    (Array.isArray(term.compatibleIncentiveIds) && term.compatibleIncentiveIds.length > 0
        ? term.compatibleIncentiveIds
        : term.incentivesContext?.compatibleIncentiveIds) ?? [];

const dedupeIds = (ids: number[]): number[] => Array.from(new Set(ids));

const getAppliedNodeIds = (term: GenericObject): number[] =>
    dedupeIds(
        parsePrefixedIds([
            ...((term.appliedIncentives?.conditionals as string[]) ?? []),
            ...((term.appliedIncentives?.nonConditionals as string[]) ?? [])
        ])
    );

const normalizeAppliedIncentiveIds = (term: GenericObject, fillFromAppliedNodes: boolean): GenericObject | undefined => {
    const applied = term.appliedIncentiveIds as number[] | undefined;
    if (applied == null) {
        if (!fillFromAppliedNodes) {
            return undefined;
        }
        const nodeIds = getAppliedNodeIds(term);
        return nodeIds.length > 0 ? { ...term, appliedIncentiveIds: nodeIds } : undefined;
    }
    const deduped = dedupeIds(applied);
    return deduped.length === applied.length ? undefined : { ...term, appliedIncentiveIds: deduped };
};

const hasDuplicateKeys = (applied: GenericObject): boolean =>
    [applied.conditionals, applied.nonConditionals].some((list) => Array.isArray(list) && new Set(list).size !== list.length);

const normalizeAppliedIncentiveKeyLists = (term: GenericObject): GenericObject | undefined => {
    const applied = term.appliedIncentives as GenericObject | undefined;
    if (applied == null || !hasDuplicateKeys(applied)) {
        return undefined;
    }
    return {
        ...term,
        appliedIncentives: {
            ...applied,
            ...(Array.isArray(applied.conditionals) && { conditionals: Array.from(new Set(applied.conditionals)) }),
            ...(Array.isArray(applied.nonConditionals) && { nonConditionals: Array.from(new Set(applied.nonConditionals)) })
        }
    };
};

const normalizeLoadedTerm = (term: GenericObject, fillFromAppliedNodes: boolean): GenericObject | undefined => {
    const withIds = normalizeAppliedIncentiveIds(term, fillFromAppliedNodes);
    const withKeys = normalizeAppliedIncentiveKeyLists(withIds ?? term);
    return withKeys ?? withIds;
};

const migrateLegacyAppliedIncentiveIds = (terms: Terms, fillFromAppliedNodes: boolean): Terms => {
    const byId = terms.byId;
    if (byId == null) {
        return terms;
    }
    let changed = false;
    const nextById = Object.fromEntries(
        Object.entries(byId).map(([termId, term]) => {
            const normalized = normalizeLoadedTerm(term as GenericObject, fillFromAppliedNodes);
            if (normalized == null) {
                return [termId, term];
            }
            changed = true;
            return [termId, normalized];
        })
    );
    return changed ? ({ ...terms, byId: nextById } as Terms) : terms;
};

const resolveNonConditionalBase = (
    echoNonConditionalIds: number[],
    catalogNonConditionalIds: number[],
    preferAppliedSet: boolean,
    trustEmptyNonConditionalEcho: boolean,
    standardsEchoIsAuthoritative: boolean
): number[] => {
    if (standardsEchoIsAuthoritative) return echoNonConditionalIds;
    if (preferAppliedSet) {
        return echoNonConditionalIds.length > 0 || trustEmptyNonConditionalEcho ? echoNonConditionalIds : catalogNonConditionalIds;
    }
    return catalogNonConditionalIds.length > 0 ? catalogNonConditionalIds : echoNonConditionalIds;
};

const unionIds = (first: number[], second: number[]): number[] => dedupeIds([...first, ...second]);

const syncAppliedConditionalIncentives = (
    term: GenericObject,
    preferAppliedSet: boolean,
    trustEmptyNonConditionalEcho: boolean,
    standardsEchoIsAuthoritative: boolean
): GenericObject => {
    const catalogNonConditionalIds = getCatalogNonConditionalIds(term);
    const catalogConditionalIds = getTermConditionalIds(term);
    const conditionalIdSet = new Set(catalogConditionalIds);
    const compatibleIdSet = new Set(getTermCompatibleIds(term));
    const isCompatibleNonConditional = (id: number): boolean => compatibleIdSet.size === 0 || compatibleIdSet.has(id);
    const appliedIds = (term.appliedIncentiveIds ?? []) as number[];
    const echoNonConditionalIds = appliedIds.filter((id) => !conditionalIdSet.has(id) && isCompatibleNonConditional(id));
    const echoConditionalIds = appliedIds.filter((id) => conditionalIdSet.has(id));
    const nonConditionalBase = resolveNonConditionalBase(
        echoNonConditionalIds,
        catalogNonConditionalIds,
        preferAppliedSet,
        trustEmptyNonConditionalEcho,
        standardsEchoIsAuthoritative
    );
    return { ...term, appliedIncentiveIds: unionIds(nonConditionalBase, echoConditionalIds) };
};

const getCatalogBackedIds = (term: GenericObject): Set<number> =>
    new Set<number>([...getTermConditionalIds(term), ...getCatalogNonConditionalIds(term)]);

const dropUnbackedAppliedIncentives = (term: GenericObject, manualBackedIds: Set<number>): GenericObject => {
    const backed = getCatalogBackedIds(term);
    const applied = term.appliedIncentiveIds as number[];
    return { ...term, appliedIncentiveIds: applied.filter((id) => backed.has(id) || manualBackedIds.has(id)) };
};

const syncSelectedTermAppliedIncentives = (
    terms: Terms,
    penciledInputs: PenciledInputsState,
    offerType: OfferType,
    preferAppliedSet: boolean,
    dropUnbacked = false,
    trustEmptyNonConditionalEcho = false,
    standardsEchoIsAuthoritative = false
): Terms => {
    const offerInputs = penciledInputs.byOfferType?.[offerType];
    if (offerInputs == null) {
        return terms;
    }
    const selectedTermId = getOfferTypeTermId(penciledInputs, offerType);
    if (selectedTermId == null || terms.byId?.[selectedTermId] == null) {
        return terms;
    }
    const synced = syncAppliedConditionalIncentives(
        terms.byId[selectedTermId],
        preferAppliedSet,
        trustEmptyNonConditionalEcho,
        standardsEchoIsAuthoritative
    );
    return {
        ...terms,
        byId: {
            ...terms.byId,
            [selectedTermId]: dropUnbacked
                ? dropUnbackedAppliedIncentives(synced, getOverriddenIncentiveIds(penciledInputs, offerType))
                : synced
        }
    } as Terms;
};

const isProgramLossResponse = (prev: GenericObject | undefined, merged: GenericObject | undefined): boolean =>
    Boolean(prev?.lenderProgram?.programName) && !merged?.lenderProgram?.programName;

const hasIncentiveCatalog = (term?: GenericObject): boolean =>
    ((term?.incentives?.conditionals as unknown[] | undefined)?.length ?? 0) +
        ((term?.incentives?.nonConditionals as unknown[] | undefined)?.length ?? 0) >
    0;

const findReKeyedPrevTerm = (
    prevTerms: Terms,
    merged: GenericObject | undefined,
    termId: string,
    offerType?: OfferType,
    selectedTermId?: string
): GenericObject | undefined => {
    if (offerType == null || termId !== selectedTermId) {
        return undefined;
    }
    return findPrevTermByMonths(prevTerms, offerType, merged?.months);
};

const findCatalogForReKeyedTerm = (
    prevTerms: Terms,
    merged: GenericObject | undefined,
    termId: string,
    offerType?: OfferType,
    selectedTermId?: string
): GenericObject | undefined =>
    hasIncentiveCatalog(merged) ? undefined : findReKeyedPrevTerm(prevTerms, merged, termId, offerType, selectedTermId);

const resolveCatalogSource = (
    prevTerms: Terms,
    prev: GenericObject | undefined,
    merged: GenericObject,
    termId: string,
    trustMergedCatalog: boolean,
    offerType?: OfferType,
    selectedTermId?: string
): GenericObject | undefined => {
    if (trustMergedCatalog && hasIncentiveCatalog(merged)) {
        return undefined;
    }
    return prev?.incentives != null ? prev : findCatalogForReKeyedTerm(prevTerms, merged, termId, offerType, selectedTermId);
};

const findProgramSourceForReKeyedTerm = (
    prevTerms: Terms,
    merged: GenericObject | undefined,
    termId: string,
    offerType?: OfferType,
    selectedTermId?: string
): GenericObject | undefined =>
    Boolean(merged?.lenderProgram?.programName) || prevTerms.byId?.[termId] != null
        ? undefined
        : findReKeyedPrevTerm(prevTerms, merged, termId, offerType, selectedTermId);

const applyCatalogSource = (merged: GenericObject, catalogSource?: GenericObject): GenericObject =>
    catalogSource?.incentives
        ? {
              ...merged,
              incentives: catalogSource.incentives,
              ...(catalogSource.incentivesContext && { incentivesContext: catalogSource.incentivesContext })
          }
        : merged;

const fillMissingCreditTier = (merged: GenericObject, source: GenericObject): GenericObject => ({
    ...(merged.creditTier == null && source.creditTier != null && { creditTier: source.creditTier })
});

const carryProgramFromSource = (merged: GenericObject, source: GenericObject): GenericObject =>
    source.lenderProgram?.programName
        ? {
              ...merged,
              lenderProgram: source.lenderProgram,
              lenderLegalName: source.lenderLegalName ?? merged.lenderLegalName,
              ...fillMissingCreditTier(merged, source)
          }
        : merged;

const hasEchoedAppliedSet = (term: Partial<PaymentTerm>): boolean => (term.appliedIncentiveIds?.length ?? 0) > 0;

const carryAppliedSetFromSource = (merged: Partial<PaymentTerm>, source: Partial<PaymentTerm>): Partial<PaymentTerm> =>
    hasEchoedAppliedSet(merged)
        ? merged
        : {
              ...merged,
              ...(source.appliedIncentiveIds !== undefined && { appliedIncentiveIds: source.appliedIncentiveIds }),
              ...(source.appliedIncentives !== undefined && { appliedIncentives: source.appliedIncentives })
          };

const getRecordedConditionalKeys = (penciledInputs: PenciledInputsState, offerType: OfferType): string[] =>
    getPenciledSelectedIncentiveKeys(penciledInputs, offerType).filter((key) =>
        key.startsWith(buildConditionalIncentiveKey(offerType, ''))
    );

const getSelectedConditionalIdsFromPenciled = (penciledInputs: PenciledInputsState, offerType: OfferType): number[] =>
    getRecordedConditionalKeys(penciledInputs, offerType).map(parseIncentiveId).filter(isNumericIncentiveId);

const resolveSelectionAppliedSet = (
    prev: Partial<PaymentTerm>,
    penciledInputs: PenciledInputsState,
    offerType: OfferType
): Partial<PaymentTerm> => {
    const catalogConditionalIds = new Set(getTermConditionalIds(prev));
    const catalogStandardIds = new Set(getCatalogNonConditionalIds(prev));
    const prevApplied = prev.appliedIncentiveIds ?? [];
    const standardsRecorded = hasRecordedStandardsDecision(penciledInputs, offerType);
    const conditionalsRecorded = getRecordedConditionalKeys(penciledInputs, offerType).length > 0;
    const conditionals = (
        conditionalsRecorded ? getSelectedConditionalIdsFromPenciled(penciledInputs, offerType) : prevApplied
    ).filter((id) => catalogConditionalIds.has(id));
    const standards = (standardsRecorded ? getRecordedStandardIdsFromPenciled(penciledInputs, offerType) : prevApplied).filter(
        (id) => catalogStandardIds.has(id)
    );
    const unbacked = prevApplied.filter((id) => !catalogConditionalIds.has(id) && !catalogStandardIds.has(id));
    return {
        appliedIncentiveIds: dedupeIds([...standards, ...conditionals, ...unbacked]),
        appliedIncentives: {
            conditionals: conditionals.map((id) => buildConditionalIncentiveKey(offerType, id)),
            nonConditionals: standards.map((id) => buildNonConditionalIncentiveKey(offerType, id))
        }
    };
};

const applyUpqProgramLossBypass = (
    merged: GenericObject,
    prev: GenericObject,
    appliedSource: Partial<PaymentTerm>
): GenericObject => carryAppliedSetFromSource(applyCatalogSource(carryProgramFromSource(merged, prev), prev), appliedSource);

const resolveBypassAppliedSource = (
    prev: GenericObject,
    termId: string,
    offerType?: OfferType,
    selectedTermId?: string,
    penciledInputs?: PenciledInputsState
): Partial<PaymentTerm> =>
    penciledInputs && offerType && termId === selectedTermId ? resolveSelectionAppliedSet(prev, penciledInputs, offerType) : prev;

export const PROGRAM_QUOTES_REFETCH_META = 'programQuotesRefetch';

const isProgramLookupOutcome = (action: AnyAction): boolean =>
    Boolean(action.meta?.originalAction?.meta?.[PROGRAM_QUOTES_REFETCH_META]);

const preserveTermIncentives = (
    prevTerms: Terms,
    mergedTerms: MergedTerms,
    preserveOnProgramLoss: boolean,
    offerType?: OfferType,
    selectedTermId?: string,
    carryProgramOnReKey = true,
    trustMergedCatalog = false,
    upqProgramLossBypass = false,
    penciledInputs?: PenciledInputsState
): Terms => {
    const byId = Object.keys(mergedTerms.byId).reduce((acc: GenericObject, termId) => {
        const prev = prevTerms.byId?.[termId] as GenericObject | undefined;
        const merged = mergedTerms.byId[termId];
        if (preserveOnProgramLoss && prev && isProgramLossResponse(prev, merged)) {
            acc[termId] = upqProgramLossBypass
                ? applyUpqProgramLossBypass(
                      merged,
                      prev,
                      resolveBypassAppliedSource(prev, termId, offerType, selectedTermId, penciledInputs)
                  )
                : prev;
            return acc;
        }
        const catalogSource = resolveCatalogSource(prevTerms, prev, merged, termId, trustMergedCatalog, offerType, selectedTermId);
        const withCatalog = applyCatalogSource(merged, catalogSource);
        const programSource =
            preserveOnProgramLoss && carryProgramOnReKey
                ? findProgramSourceForReKeyedTerm(prevTerms, merged, termId, offerType, selectedTermId)
                : undefined;
        acc[termId] = programSource ? carryProgramFromSource(withCatalog, programSource) : withCatalog;
        return acc;
    }, {});
    return { ...mergedTerms, byId };
};

const findPrevTermByMonths = (prevTerms: Terms, offerType: OfferType, months?: number): GenericObject | undefined => {
    const ids = getOfferTypeTerms(offerType, prevTerms) as string[];
    const sameMonthIds = ids.filter((id) => prevTerms.byId?.[id]?.months === months);
    const appliedId = sameMonthIds.find((id) => (prevTerms.byId?.[id] as GenericObject)?.lenderProgram?.programName);
    const matchId = appliedId ?? sameMonthIds[0];
    return matchId ? (prevTerms.byId?.[matchId] as GenericObject) : undefined;
};

const hasAppliedIncentives = (term?: GenericObject): boolean =>
    Boolean(term && (((term.appliedIncentiveIds as number[] | undefined)?.length ?? 0) > 0 || term.lenderProgram?.programName));

const findPrevAppliedTerm = (terms: Terms, offerType: OfferType): GenericObject | undefined => {
    const ids = getOfferTypeTerms(offerType, terms) as string[];
    const appliedId = ids.find((id) => hasAppliedIncentives(terms.byId?.[id] as GenericObject));
    return appliedId ? (terms.byId?.[appliedId] as GenericObject) : undefined;
};

const hasIncentivesContext = (term?: GenericObject): boolean => term?.incentivesContext?.programContextId != null;

// The field set dash-store's buildIncentivesContextId keys programContextId on, duplicated rather
// than imported because offer-redux cannot depend on dash-store. A context belongs to one program at
// one term length, so carrying one across a differing identity would filter the catalog on another
// program's compatibility set.
const LENDER_PROGRAM_IDENTITY_KEYS = ['branchId', 'lenderId', 'programName', 'programType', 'regionalId'] as const;

const isSameProgramIdentity = (target: GenericObject, candidate: GenericObject): boolean =>
    candidate.months === target.months &&
    LENDER_PROGRAM_IDENTITY_KEYS.every((key) => (candidate.lenderProgram?.[key] ?? '') === (target.lenderProgram?.[key] ?? ''));

const findPrevContextTerm = (terms: Terms, offerType: OfferType, target: GenericObject): GenericObject | undefined => {
    const ids = getOfferTypeTerms(offerType, terms) as string[];
    const withContext = ids.map((id) => terms.byId?.[id] as GenericObject).filter((term) => hasIncentivesContext(term));
    return withContext.find((term) => isSameProgramIdentity(target, term)) ?? withContext[0];
};

const hasIncentiveList = (term: GenericObject | undefined): boolean =>
    Boolean(term?.incentives?.conditionals?.length || term?.incentives?.nonConditionals?.length);

const INCENTIVE_LIST_GROUPS = ['conditionals', 'nonConditionals'] as const;

type IncentiveList = { conditionals?: GenericObject[]; nonConditionals?: GenericObject[] };

export const withResponseAmounts = (list: IncentiveList | undefined, priced: IncentiveList): IncentiveList => {
    if (!list) {
        return priced;
    }
    const amountById = new Map<string, unknown>();
    INCENTIVE_LIST_GROUPS.forEach((group) => (priced[group] ?? []).forEach((row) => amountById.set(String(row.id), row.amount)));
    const reprice = (rows: GenericObject[] | undefined) =>
        rows?.map((row) => (amountById.has(String(row.id)) ? { ...row, amount: amountById.get(String(row.id)) } : row));
    return { ...list, conditionals: reprice(list.conditionals), nonConditionals: reprice(list.nonConditionals) };
};

const carryAppliedIncentivesToSelectedTerm = (
    terms: Terms,
    offerType: OfferType,
    selectedTermId: string | undefined,
    gridCashDownCatalogCarryEnabled: boolean,
    keepOwnAmounts: boolean
): Terms => {
    const target = selectedTermId ? (terms.byId?.[selectedTermId] as GenericObject | undefined) : undefined;
    if (!selectedTermId || !target) {
        return terms;
    }
    if (!hasAppliedIncentives(target)) {
        const source = findPrevAppliedTerm(terms, offerType);
        if (!source) {
            return terms;
        }
        const byId: GenericObject = {
            ...terms.byId,
            [selectedTermId]: {
                ...target,
                incentives: source.incentives,
                incentivesContext: source.incentivesContext,
                appliedIncentiveIds: source.appliedIncentiveIds
            }
        };
        return { ...terms, byId };
    }
    if (!gridCashDownCatalogCarryEnabled || hasIncentivesContext(target)) {
        return terms;
    }
    const catalogSource = findPrevContextTerm(terms, offerType, target);
    if (!catalogSource) {
        return terms;
    }
    const byId: GenericObject = {
        ...terms.byId,
        [selectedTermId]: {
            ...target,
            incentives:
                keepOwnAmounts && hasIncentiveList(target)
                    ? withResponseAmounts(catalogSource.incentives, target.incentives)
                    : catalogSource.incentives,
            incentivesContext: catalogSource.incentivesContext
        }
    };
    return { ...terms, byId };
};

type AppliedIdFilter = (ids: number[]) => number[];

const pickAppliedIncentiveSource = (merged: GenericObject, prev: GenericObject | undefined, preferMerged: boolean): GenericObject =>
    preferMerged && Array.isArray(merged.appliedIncentiveIds) ? merged : prev?.appliedIncentiveIds ? prev : merged;

const hasSameIncentiveIds = (first: unknown, second: unknown): boolean =>
    Array.isArray(first) && Array.isArray(second) && first.length === second.length && first.every((id) => second.includes(id));

const toProgramIncentiveIds = (ids: unknown[], compatibleIds: Set<number> | undefined, manualIds: Set<number>): number[] =>
    ids.filter(isNumericIncentiveId).filter((id) => !manualIds.has(id) && (!compatibleIds || compatibleIds.has(id)));

const getResponseCompatibleIds = (merged: GenericObject): Set<number> | undefined => {
    const compatible = merged.compatibleIncentiveIds;
    return Array.isArray(compatible) && compatible.length > 0 ? new Set(compatible.filter(isNumericIncentiveId)) : undefined;
};

const hasSameProgramIncentiveIds = (source: GenericObject, merged: GenericObject, manualIds: Set<number>): boolean => {
    const first = source.appliedIncentiveIds;
    const second = merged.appliedIncentiveIds;
    if (!Array.isArray(first) || !Array.isArray(second)) return false;
    const compatibleIds = getResponseCompatibleIds(merged);
    return haveSameNumericIds(
        toProgramIncentiveIds(first, compatibleIds, manualIds),
        toProgramIncentiveIds(second, compatibleIds, manualIds)
    );
};

const shouldTakeResponseAmount = (
    merged: GenericObject,
    source: GenericObject,
    appliedIncentiveDisplayFixesEnabled: boolean,
    responseAppliedIncentiveAmountEnabled: boolean,
    manualIncentiveIds: Set<number>
): boolean => {
    if (source === merged || !(merged.appliedIncentiveAmount > 0)) return false;
    if (responseAppliedIncentiveAmountEnabled) {
        return hasSameProgramIncentiveIds(source, merged, manualIncentiveIds);
    }
    return appliedIncentiveDisplayFixesEnabled && hasSameIncentiveIds(source.appliedIncentiveIds, merged.appliedIncentiveIds);
};

const restoreAppliedIncentiveSet = (
    merged: GenericObject,
    prev: GenericObject | undefined,
    filterIds: AppliedIdFilter,
    preferMerged: boolean,
    appliedIncentiveDisplayFixesEnabled: boolean,
    responseAppliedIncentiveAmountEnabled: boolean,
    manualIncentiveIds: Set<number>
): GenericObject => {
    const source = pickAppliedIncentiveSource(merged, prev, preferMerged);
    const ids = source.appliedIncentiveIds;
    const amountSource = shouldTakeResponseAmount(
        merged,
        source,
        appliedIncentiveDisplayFixesEnabled,
        responseAppliedIncentiveAmountEnabled,
        manualIncentiveIds
    )
        ? merged
        : source;
    return {
        appliedIncentiveIds: Array.isArray(ids) ? filterIds(ids) : ids,
        appliedIncentiveAmount: amountSource.appliedIncentiveAmount
    };
};

const restoreUnrequestedIncentives = (
    merged: GenericObject,
    prev: GenericObject | undefined,
    filterIds: AppliedIdFilter,
    preferMerged: boolean,
    appliedIncentiveDisplayFixesEnabled: boolean,
    responseAppliedIncentiveAmountEnabled: boolean,
    manualIncentiveIds: Set<number>
): GenericObject => {
    const keepMergedProgram = Boolean(merged.lenderProgram?.programName);
    return {
        ...merged,
        lenderProgram: keepMergedProgram ? merged.lenderProgram : prev?.lenderProgram ?? merged.lenderProgram,
        lenderLegalName: keepMergedProgram
            ? merged.lenderLegalName ?? prev?.lenderLegalName
            : prev?.lenderLegalName ?? merged.lenderLegalName,
        incentives: prev?.incentives ?? merged.incentives,
        incentivesContext: prev?.incentivesContext ?? merged.incentivesContext,
        ...restoreAppliedIncentiveSet(
            merged,
            prev,
            filterIds,
            preferMerged,
            appliedIncentiveDisplayFixesEnabled,
            responseAppliedIncentiveAmountEnabled,
            manualIncentiveIds
        ),
        appliedIncentives: prev?.appliedIncentives ?? merged.appliedIncentives
    };
};

const preserveUnrequestedTermIncentives = (
    prevTerms: Terms,
    mergedTerms: MergedTerms,
    offerType: OfferType,
    penciledInputs: PenciledInputsState,
    preferServiceAppliedIds: boolean,
    selectedTermId: string | undefined,
    appliedIncentiveDisplayFixesEnabled: boolean,
    responseAppliedIncentiveAmountEnabled: boolean
): Terms => {
    const filterIds = buildConvertedIdFilter();
    const manualIncentiveIds = responseAppliedIncentiveAmountEnabled
        ? getOverriddenIncentiveIds(penciledInputs, offerType)
        : new Set<number>();
    const byId = Object.keys(mergedTerms.byId).reduce((acc: GenericObject, termId) => {
        const merged = mergedTerms.byId[termId];
        const exact = prevTerms.byId?.[termId] as GenericObject | undefined;
        const prev = exact ?? (termId === selectedTermId ? findPrevTermByMonths(prevTerms, offerType, merged?.months) : undefined);
        acc[termId] = restoreUnrequestedIncentives(
            merged,
            prev,
            filterIds,
            preferServiceAppliedIds,
            appliedIncentiveDisplayFixesEnabled,
            responseAppliedIncentiveAmountEnabled,
            manualIncentiveIds
        );
        return acc;
    }, {});
    return dropUnrecordedStandardsFromSelectedTerm({ ...mergedTerms, byId } as Terms, penciledInputs, offerType, selectedTermId);
};

const reconcileMergedTermIncentives = (
    prevTerms: Terms,
    mergedTerms: MergedTerms,
    penciledInputs: PenciledInputsState,
    offerType: OfferType,
    incentivesRequested: boolean,
    carryProgramOnReKey: boolean,
    standardsSeededByService: boolean,
    upqProgramLossBypass: boolean,
    appliedIncentiveDisplayFixesEnabled: boolean,
    responseAppliedIncentiveAmountEnabled: boolean
): Terms =>
    incentivesRequested
        ? syncSelectedTermAppliedIncentives(
              preserveTermIncentives(
                  prevTerms,
                  mergedTerms,
                  true,
                  offerType,
                  getOfferTypeTermId(penciledInputs, offerType),
                  carryProgramOnReKey,
                  true,
                  upqProgramLossBypass,
                  penciledInputs
              ),
              penciledInputs,
              offerType,
              true,
              false,
              true
          )
        : preserveUnrequestedTermIncentives(
              prevTerms,
              mergedTerms,
              offerType,
              penciledInputs,
              standardsSeededByService,
              getOfferTypeTermId(penciledInputs, offerType),
              appliedIncentiveDisplayFixesEnabled,
              responseAppliedIncentiveAmountEnabled
          );

const syncSelectedTermFromAuthoritativeEcho = (terms: Terms, penciledInputs: PenciledInputsState, offerType: OfferType): Terms =>
    syncSelectedTermAppliedIncentives(terms, penciledInputs, offerType, true, true, false, true);

const isVehicleSwitchRecalc = (action: AnyAction): boolean => action.meta?.originalAction?.type === types.SWITCH_VEHICLE;

const didServiceSeedStandards = (action: AnyAction): boolean => Boolean(action.meta?.includeStandardIncentives);

const getTermStandardIds = (term: GenericObject): Set<number> =>
    new Set([
        ...getCatalogNonConditionalIds(term),
        ...parsePrefixedIds(((term.appliedIncentives?.nonConditionals as Array<string | number>) ?? []).map(String))
    ]);

const keepOnlyResponseAppliedStandards = (term: GenericObject, responseTerm: GenericObject): GenericObject | undefined => {
    const standardIds = getTermStandardIds(term);
    const responseAppliedIds = new Set<number>((responseTerm.appliedIncentiveIds as number[] | undefined) ?? []);
    const isKept = (id: number): boolean => !standardIds.has(id) || responseAppliedIds.has(id);
    const appliedIds = (term.appliedIncentiveIds as number[] | undefined) ?? [];
    const appliedKeys = (term.appliedIncentives?.nonConditionals as Array<string | number> | undefined) ?? [];
    const keptIds = appliedIds.filter(isKept);
    const keptKeys = appliedKeys.filter((entry) => isKept(parseIncentiveId(String(entry))));
    if (keptIds.length === appliedIds.length && keptKeys.length === appliedKeys.length) {
        return undefined;
    }
    return {
        ...term,
        appliedIncentiveIds: keptIds,
        appliedIncentiveAmount: responseTerm.appliedIncentiveAmount,
        appliedIncentives: { ...term.appliedIncentives, nonConditionals: keptKeys }
    };
};

const dropStandardsTheResponseDidNotApply = (terms: Terms, action: AnyAction, offerType: OfferType): Terms => {
    if (!action.meta?.dealerStandardsExcluded) {
        return terms;
    }
    const responseById = (action.payload.terms.byId ?? {}) as Record<string, GenericObject>;
    const byId: GenericObject = { ...terms.byId };
    let changed = false;
    Object.keys(responseById)
        .filter((termId) => termId.split('_')[0] === offerType)
        .forEach((termId) => {
            const kept = keepOnlyResponseAppliedStandards(byId[termId], responseById[termId]);
            if (kept) {
                byId[termId] = kept;
                changed = true;
            }
        });
    return changed ? ({ ...terms, byId } as Terms) : terms;
};

const isIncentiveDrivenRecalc = (action: AnyAction): boolean => isIncentiveChangeActionType(action.meta?.originalAction?.type);

const responseTermHasAppliedIncentiveIds = (
    action: AnyAction,
    penciledInputs: PenciledInputsState,
    offerType: OfferType
): boolean => {
    const termId = getOfferTypeTermId(penciledInputs, offerType);
    const responseTerm = action.payload?.terms?.byId?.[termId as string] as GenericObject | undefined;
    return Array.isArray(responseTerm?.appliedIncentiveIds);
};

const getPenciledSelectedIncentiveKeys = (penciledInputs: PenciledInputsState, offerType: OfferType): string[] => {
    const selected = (penciledInputs?.byOfferType?.[offerType] as GenericObject | undefined)?.selectedIncentives ?? [];
    return (selected as unknown[]).filter((key): key is string => typeof key === 'string');
};

const getNonConditionalKeyPrefix = (offerType: OfferType): string => buildNonConditionalIncentiveKey(offerType, '');

const isRecordedStandardKey = (key: string, offerType: OfferType): boolean => key.startsWith(getNonConditionalKeyPrefix(offerType));

const hasRecordedStandardsDecision = (penciledInputs: PenciledInputsState, offerType: OfferType): boolean =>
    getPenciledSelectedIncentiveKeys(penciledInputs, offerType).some((key) => isRecordedStandardKey(key, offerType));

const canTrustVehicleSwitchEcho = (action: AnyAction, penciledInputs: PenciledInputsState, offerType: OfferType): boolean =>
    hasRecordedStandardsDecision(penciledInputs, offerType) &&
    responseTermHasAppliedIncentiveIds(action, penciledInputs, offerType);

const syncVehicleSwitchTermIncentives = (
    merged: Terms,
    action: AnyAction,
    penciledInputs: PenciledInputsState,
    offerType: OfferType
): Terms =>
    canTrustVehicleSwitchEcho(action, penciledInputs, offerType)
        ? syncSelectedTermFromAuthoritativeEcho(merged, penciledInputs, offerType)
        : syncSelectedTermAppliedIncentives(merged, penciledInputs, offerType, true, true);

const isNumericIncentiveId = (id: unknown): id is number => typeof id === 'number' && !Number.isNaN(id);

const haveSameNumericIds = (a: number[] = [], b: number[]): boolean => {
    if (a.length !== b.length) {
        return false;
    }
    const setA = new Set(a);
    return b.every((id) => setA.has(id));
};

const getOverriddenIncentiveIds = (penciledInputs: PenciledInputsState, offerType: OfferType): Set<number> => {
    const incentives = (penciledInputs?.byOfferType?.[offerType] as GenericObject | undefined)?.manualIncentives?.incentives ?? [];
    return getOverriddenIncentiveCodes(incentives as Array<{ code?: number; manual?: boolean }>);
};

const getRecordedStandardIdsFromPenciled = (penciledInputs: PenciledInputsState, offerType: OfferType): number[] =>
    getPenciledSelectedIncentiveKeys(penciledInputs, offerType)
        .filter((key) => isRecordedStandardKey(key, offerType))
        .map(parseIncentiveId)
        .filter(isNumericIncentiveId);

const buildConvertedIdFilter = (): AppliedIdFilter => (ids) => ids;

// A recorded standards decision is authoritative: seeding must not resurrect a standard the user
// unchecked just because a stale penciled applied node or the term catalog still lists it.
const dropUnrecordedStandards = (
    ids: number[],
    penciledInputs: PenciledInputsState,
    offerType: OfferType,
    standardIds: Set<number>,
    echoedAppliedIds: number[]
): number[] => {
    if (!hasRecordedStandardsDecision(penciledInputs, offerType)) {
        return ids;
    }
    const recorded = new Set([...getRecordedStandardIdsFromPenciled(penciledInputs, offerType), ...echoedAppliedIds]);
    return ids.filter((id) => !standardIds.has(id) || recorded.has(id));
};

/**
 * Reconciles the selected term's applied set against a recorded standards decision.
 *
 * Needed because a recalc that is not authoritative about incentives restores the applied set verbatim
 * from the previous record (`preserveUnrequestedTermIncentives`), which still names a standard the
 * operator has since unchecked - leaving the Programs & Incentives panel rendering it as selected.
 *
 * Applied to the selected term ONLY: a recorded decision describes the live selection, while every other
 * record is a snapshot of what was applied when that term was last priced authoritatively.
 *
 * Unlike `dropUnrecordedStandards` this takes no echoed-ids exemption - here the term's own stale value is
 * precisely what has to be dropped.
 */
const dropUnrecordedStandardsFromSelectedTerm = (
    terms: Terms,
    penciledInputs: PenciledInputsState,
    offerType: OfferType,
    selectedTermId?: string
): Terms => {
    const term = selectedTermId == null ? undefined : (terms.byId?.[selectedTermId] as GenericObject | undefined);
    if (term == null || !hasRecordedStandardsDecision(penciledInputs, offerType)) {
        return terms;
    }
    const ids = term.appliedIncentiveIds;
    if (!Array.isArray(ids)) {
        return terms;
    }
    const standardIds = new Set(getCatalogNonConditionalIds(term));
    const recorded = new Set(getRecordedStandardIdsFromPenciled(penciledInputs, offerType));
    const kept = (ids as number[]).filter((id) => !standardIds.has(id) || recorded.has(id));
    if (kept.length === ids.length) {
        return terms;
    }
    const keptSet = new Set(kept);
    const appliedNonConditionals = (term.appliedIncentives?.nonConditionals ?? []) as Array<string | number>;
    return {
        ...terms,
        byId: {
            ...terms.byId,
            [selectedTermId as string]: {
                ...term,
                appliedIncentiveIds: kept,
                // The amount and the itemized set both described the dropped rows, so they go with them.
                // The display and the request then resolve the total themselves rather than trusting a
                // figure that no longer matches the set beside it.
                appliedIncentiveAmount: undefined,
                appliedIncentives: {
                    ...term.appliedIncentives,
                    nonConditionals: appliedNonConditionals.filter((entry) => {
                        const id = parseIncentiveId(String(entry));
                        return Number.isNaN(id) || !standardIds.has(id) || keptSet.has(id);
                    })
                }
            }
        }
    } as Terms;
};

export const seedSelectedTermAppliedIncentivesFromPenciled = (
    terms: Terms,
    penciledInputs: PenciledInputsState,
    offerType: OfferType
): Terms => {
    if (offerType === CASH) {
        return terms;
    }
    const offerInputs = penciledInputs.byOfferType?.[offerType] as GenericObject | undefined;
    const selectedTermId = offerInputs?.term as string | undefined;
    const term = selectedTermId ? (terms.byId?.[selectedTermId] as GenericObject | undefined) : undefined;
    if (!selectedTermId || !term) {
        return terms;
    }
    const penciledApplied = offerInputs?.appliedIncentiveIds;
    const penciledIds = (Array.isArray(penciledApplied) ? penciledApplied : []).filter(isNumericIncentiveId);
    if (penciledIds.length === 0) {
        return terms;
    }
    const standardIds = new Set(getCatalogNonConditionalIds(term));
    const overriddenIds = getOverriddenIncentiveIds(penciledInputs, offerType);
    const catalogConditionalIds = new Set(getTermConditionalIds(term));
    const preservedApplied = ((term.appliedIncentiveIds as number[] | undefined) ?? []).filter(
        (id) => overriddenIds.has(id) || standardIds.has(id) || catalogConditionalIds.has(id)
    );
    const seededIds = Array.from(new Set([...penciledIds, ...preservedApplied]));
    const applied = dropUnrecordedStandards(seededIds, penciledInputs, offerType, standardIds, preservedApplied);
    if (haveSameNumericIds(term.appliedIncentiveIds as number[] | undefined, applied)) {
        return terms;
    }
    return {
        ...terms,
        byId: { ...terms.byId, [selectedTermId]: { ...term, appliedIncentiveIds: applied } }
    } as Terms;
};

const dropNonCompatibleCatalogIncentives = (terms: Terms, penciledInputs: PenciledInputsState, offerType: OfferType): Terms => {
    const selectedTermId = getOfferTypeTermId(penciledInputs, offerType);
    const term = selectedTermId == null ? undefined : (terms.byId?.[selectedTermId] as GenericObject | undefined);
    if (selectedTermId == null || term == null) {
        return terms;
    }
    const compatibleIds = getTermCompatibleIds(term);
    if (compatibleIds.length === 0) {
        return terms;
    }
    const compatibleIdSet = new Set(compatibleIds);
    const catalogIdSet = getCatalogBackedIds(term);
    const applied = (term.appliedIncentiveIds ?? []) as number[];
    const filtered = applied.filter((id) => compatibleIdSet.has(id) || !catalogIdSet.has(id));
    if (haveSameNumericIds(applied, filtered)) {
        return terms;
    }
    return {
        ...terms,
        byId: { ...terms.byId, [selectedTermId]: { ...term, appliedIncentiveIds: filtered } }
    } as Terms;
};

const getTermConditionalExclusions = (term: GenericObject): Map<number, Set<number>> => {
    const map = new Map<number, Set<number>>();
    ((term.incentives?.conditionals ?? []) as Array<{ id: string; exclusions?: number[] }>).forEach((incentive) => {
        map.set(parseIncentiveId(incentive.id), new Set(incentive.exclusions ?? []));
    });
    return map;
};

const isExcludedByKept = (exclusions: Map<number, Set<number>>, kept: number[], id: number, overriddenIds: Set<number>): boolean =>
    kept.some(
        (keptId) =>
            !overriddenIds.has(keptId) && (Boolean(exclusions.get(keptId)?.has(id)) || Boolean(exclusions.get(id)?.has(keptId)))
    );

const pruneExclusiveConditionals = (term: GenericObject, overriddenIds: Set<number>): GenericObject => {
    const exclusions = getTermConditionalExclusions(term);
    if (exclusions.size === 0) {
        return term;
    }
    const applied = (term.appliedIncentiveIds ?? []) as number[];
    const kept = applied.reduce((acc: number[], id) => {
        const isConditional = exclusions.has(id) && !overriddenIds.has(id);
        if (!isConditional || !isExcludedByKept(exclusions, acc, id, overriddenIds)) {
            acc.push(id);
        }
        return acc;
    }, []);
    if (haveSameNumericIds(applied, kept)) {
        return term;
    }
    return { ...term, appliedIncentiveIds: kept };
};

const dropExclusiveConditionalIncentives = (terms: Terms, penciledInputs: PenciledInputsState, offerType: OfferType): Terms => {
    const selectedTermId = getOfferTypeTermId(penciledInputs, offerType);
    const term = selectedTermId == null ? undefined : (terms.byId?.[selectedTermId] as GenericObject | undefined);
    if (selectedTermId == null || term == null) {
        return terms;
    }
    const pruned = pruneExclusiveConditionals(term, getOverriddenIncentiveIds(penciledInputs, offerType));
    if (pruned === term) {
        return terms;
    }
    return {
        ...terms,
        byId: { ...terms.byId, [selectedTermId]: pruned }
    } as Terms;
};

/**
 * DE760840 (AC3): keep the selected term's *applied* incentive set when the response being merged was
 * requested for a term the user has since moved off of.
 *
 * Scoped deliberately to `appliedIncentives` / `appliedIncentiveIds`. The per-term `incentives` catalog is
 * already protected by `preserveTermIncentives`, but the applied set is taken straight from the response, so
 * a superseded call can clear the incentive applied to the current term. Everything else in the merge
 * (rates, residual, program, payments) still applies, unlike `meta.isStalePaymentResponse`, which drops the
 * response entirely and is gated on enableLeaseResidualTreatment_F286227.
 */
const preserveSelectedTermAppliedIncentives = (
    prevTerms: Terms,
    mergedTerms: Terms,
    action: AnyAction,
    penciledInputs: PenciledInputsState,
    offerType: OfferType,
    currentTermId?: string
): Terms => {
    // Read directly rather than via getOfferTypeTermId, which indexes both levels unguarded; neither is
    // guaranteed for every action reaching this reducer.
    const selectedTermId = currentTermId ?? (penciledInputs as GenericObject)?.byOfferType?.[offerType]?.term;
    if (!isSupersededTermPaymentResponse(action, selectedTermId)) {
        return mergedTerms;
    }
    const termId = selectedTermId as string;
    const prev = prevTerms.byId?.[termId] as GenericObject | undefined;
    const merged = mergedTerms.byId?.[termId] as GenericObject | undefined;
    if (!prev || !merged) {
        return mergedTerms;
    }
    return {
        ...mergedTerms,
        byId: {
            ...mergedTerms.byId,
            [termId]: {
                ...merged,
                ...(prev.appliedIncentives !== undefined && { appliedIncentives: prev.appliedIncentives }),
                ...(prev.appliedIncentiveIds !== undefined && { appliedIncentiveIds: prev.appliedIncentiveIds })
            }
        }
    } as Terms;
};

const isRegularGridRowResponse = (action: AnyAction): boolean => isRegularGridRowAction(action.meta?.originalAction);

const rebuildAppliedIncentives = (record: GenericObject, ids: number[]): GenericObject => {
    const applied = new Set(ids);
    const keysOf = (group: 'conditionals' | 'nonConditionals'): string[] => {
        const recorded: unknown[] = record.appliedIncentives?.[group] ?? [];
        const listed: unknown[] = (record.incentives?.[group] ?? []).map((row: GenericObject) => row.id);
        return Array.from(new Set([...recorded, ...listed])).filter(
            (key): key is string => typeof key === 'string' && applied.has(parseIncentiveId(key))
        );
    };
    return { ...record.appliedIncentives, conditionals: keysOf('conditionals'), nonConditionals: keysOf('nonConditionals') };
};

const keepRecordIncentiveList = (record: GenericObject | undefined, adopted: GenericObject): GenericObject => {
    if (hasIncentiveList(adopted) && hasIncentiveList(record)) {
        const incentives = withResponseAmounts(record?.incentives, adopted.incentives);
        return { ...adopted, incentives, incentivesContext: adopted.incentivesContext ?? record?.incentivesContext };
    }
    if (hasIncentiveList(adopted) || !hasIncentiveList(record)) {
        return adopted;
    }
    const listed = record as GenericObject;
    const withList = {
        ...adopted,
        incentives: listed.incentives,
        incentivesContext: adopted.incentivesContext ?? listed.incentivesContext
    };
    return Array.isArray(adopted.appliedIncentiveIds)
        ? { ...withList, appliedIncentives: rebuildAppliedIncentives(withList, adopted.appliedIncentiveIds) }
        : withList;
};

const adoptGridResponseTermIncentives = (terms: Terms, action: AnyAction, selectedTermId: string | undefined): Terms => {
    const responseById: Record<string, GenericObject> = action.payload?.terms?.byId ?? {};
    const deal = selectedTermId ? (terms.byId?.[selectedTermId] as GenericObject | undefined) : undefined;
    const dealList = hasIncentiveList(deal) ? deal : undefined;
    const byId = Object.entries(responseById).reduce(
        (acc: GenericObject, [termId, response]) => {
            if (termId === selectedTermId || !Array.isArray(response.appliedIncentiveIds)) {
                return acc;
            }
            const priced = hasIncentiveList(response);
            const incentives = priced
                ? withResponseAmounts(
                      (dealList?.incentives ?? acc[termId]?.incentives) as IncentiveList | undefined,
                      response.incentives
                  )
                : dealList?.incentives;
            const record =
                dealList || priced
                    ? { ...acc[termId], incentives, ...(dealList && { incentivesContext: dealList.incentivesContext }) }
                    : acc[termId];
            acc[termId] = {
                ...record,
                appliedIncentiveIds: response.appliedIncentiveIds,
                appliedIncentiveAmount: response.appliedIncentiveAmount,
                appliedIncentives: rebuildAppliedIncentives(record, response.appliedIncentiveIds)
            };
            return acc;
        },
        { ...terms.byId }
    );
    return { ...terms, byId } as Terms;
};

const MAX_UNPRICED_ROW_SEARCH = 6;

const toCents = (value: unknown): number => Math.round((Number(value) || 0) * 100);

const findUnpricedRowSet = (amounts: number[], excess: number): number[] | undefined => {
    if (amounts.length > MAX_UNPRICED_ROW_SEARCH) return undefined;
    for (let size = 1; size <= amounts.length; size++) {
        const matches: number[][] = [];
        const visit = (start: number, picked: number[], sum: number) => {
            if (picked.length === size) {
                if (sum === excess) matches.push(picked);
                return;
            }
            for (let index = start; index < amounts.length; index++) visit(index + 1, [...picked, index], sum + amounts[index]);
        };
        visit(0, [], 0);
        if (matches.length === 1) return matches[0];
        if (matches.length > 1) return undefined;
    }
    return undefined;
};

const zeroUnpricedRowsOfTerm = (
    record: GenericObject,
    catalog: Record<string, GenericObject>,
    manualCents: number
): GenericObject => {
    const appliedIds = record.appliedIncentiveIds;
    if (!Array.isArray(appliedIds) || typeof record.appliedIncentiveAmount !== 'number') return record;
    const applied = new Set(appliedIds);
    const candidates: Array<{ key: string; cents: number }> = [];
    let listedCents = 0;
    INCENTIVE_LIST_GROUPS.forEach((group) =>
        ((record.incentives?.[group] ?? []) as Array<{ id: string; amount?: number }>).forEach((row, index) => {
            if (!applied.has(parseIncentiveId(row.id)) || isStoredDealerCash({ ...catalog[row.id], ...row } as GenericObject))
                return;
            const cents = toCents(row.amount ?? catalog[row.id]?.amount);
            listedCents += cents;
            if (cents > 0) candidates.push({ key: `${group}:${index}`, cents });
        })
    );
    const excess = listedCents - (toCents(record.appliedIncentiveAmount) - manualCents);
    const picked =
        excess > 0
            ? findUnpricedRowSet(
                  candidates.map((candidate) => candidate.cents),
                  excess
              )
            : undefined;
    if (!picked) return record;
    const zeroed = new Set(picked.map((index) => candidates[index].key));
    const incentives = { ...record.incentives };
    INCENTIVE_LIST_GROUPS.forEach((group) => {
        if (incentives[group]) {
            incentives[group] = incentives[group].map((row: GenericObject, index: number) =>
                zeroed.has(`${group}:${index}`) ? { ...row, amount: 0 } : row
            );
        }
    });
    return { ...record, incentives };
};

const getUnpricedRowCheckTermIds = (action: AnyAction, penciledInputs: PenciledInputsState, offerType: OfferType): string[] => {
    if (action.type === types.PAYMENT_RECEIVED) {
        return action.meta?.requestCalculationEndpoint === CALC_ENDPOINTS.programQuotes
            ? Object.keys(action.payload?.terms?.byId ?? {})
            : [];
    }
    const selectedTermId =
        action.type === types.UPDATE_TERM_AVAILABLE_INCENTIVES ? getPenciledTermId(penciledInputs, offerType) : undefined;
    return selectedTermId ? [selectedTermId] : [];
};

export const zeroUnpricedAppliedIncentiveRows = (
    terms: Terms,
    action: AnyAction,
    incentives: { byId?: Record<string, GenericObject> } | undefined,
    penciledInputs: PenciledInputsState,
    offerType: OfferType
): Terms => {
    const termIds = getUnpricedRowCheckTermIds(action, penciledInputs, offerType);
    if (termIds.length === 0) {
        return terms;
    }
    const manualRows = ((penciledInputs.byOfferType?.[offerType] as GenericObject | undefined)?.manualIncentives?.incentives ??
        []) as Array<GenericObject>;
    const manualCents = manualRows
        .filter((row) => !row.deleted && isManualIncentiveByProvenance(row as never) && !isStoredDealerCash(row))
        .reduce((sum, row) => sum + toCents(row.amount), 0);
    const catalog = incentives?.byId ?? {};
    let changed = false;
    const byId: GenericObject = { ...terms.byId };
    termIds.forEach((termId) => {
        const record = byId[termId] as GenericObject | undefined;
        if (!record) return;
        const zeroed = zeroUnpricedRowsOfTerm(record, catalog, manualCents);
        if (zeroed !== record) {
            byId[termId] = zeroed;
            changed = true;
        }
    });
    return changed ? ({ ...terms, byId } as Terms) : terms;
};

const adoptSelectedTermResponseIncentives = (
    terms: Terms,
    action: AnyAction,
    penciledInputs: PenciledInputsState,
    offerType: OfferType,
    selectedTermId: string | undefined
): Terms => {
    const termId = selectedTermId as string;
    const response = action.payload?.terms?.byId?.[termId] as GenericObject | undefined;
    const record = (terms.byId as GenericObject)[termId] as GenericObject | undefined;
    if (
        !record ||
        !Array.isArray(response?.appliedIncentiveIds) ||
        action.meta?.requestCalculationEndpoint !== CALC_ENDPOINTS.programQuotes ||
        isSupersededTermPaymentResponse(action, termId)
    ) {
        return terms;
    }
    const overriddenIds = getOverriddenIncentiveIds(penciledInputs, offerType);
    const keptOverridden = (record.appliedIncentiveIds as number[]).filter((id) => overriddenIds.has(id));
    const ids = Array.from(new Set([...(response.appliedIncentiveIds as number[]), ...keptOverridden]));
    return {
        ...terms,
        byId: {
            ...terms.byId,
            [termId]: {
                ...record,
                appliedIncentiveIds: ids,
                appliedIncentiveAmount: response.appliedIncentiveAmount,
                appliedIncentives: rebuildAppliedIncentives(record, ids)
            }
        }
    } as Terms;
};

/**
 * DE785177: a grid row reply that reports no applied ids carries the rebate its request sent, as an amount. When
 * the rebate ledger changed while that request was in flight (prod: a late-landing profit recalc re-froze it
 * empty), the reply was priced on a rebate the deal no longer holds; written to the selected term, Amt. Financed
 * deducts a rebate that Rebates (read from the ledger) does not show, and the deal saves that way. The selected
 * term keeps what the authoritative response settled; the reply's other cells still apply.
 *
 * The test is "did the ledger change", not "does the amount match the ledger": the request sends a recomputed
 * total (term rebates, overridden and incompatible rows, the carried UPQ total), and Payment Services can cap
 * it, so a good reply's amount can differ from the ledger's total legitimately.
 */
const isPricedOnSupersededLedger = (
    cell: PaymentTerm,
    ledger: LedgerAwareManualIncentives,
    requestLedgerRebates: number
): boolean =>
    Array.isArray(cell.appliedIncentiveIds) &&
    cell.appliedIncentiveIds.length === 0 &&
    sumLedgerRebates(ledger.incentives) !== requestLedgerRebates;

const withoutStaleSelectedGridCell = (
    action: AnyAction,
    penciledInputs: PenciledInputsState,
    offerType: OfferType,
    currentTermId?: string
): AnyAction => {
    const inputs = penciledInputs.byOfferType?.[offerType];
    const termId = currentTermId ?? inputs?.term;
    const ledger: LedgerAwareManualIncentives | undefined = inputs?.manualIncentives;
    const responseTerms: Terms | undefined = action.payload?.terms;
    const requestLedgerRebates: unknown = action.meta?.[REQUEST_LEDGER_REBATES_META];
    if (
        !termId ||
        !responseTerms?.byId ||
        typeof requestLedgerRebates !== 'number' ||
        !isRegularGridRowResponse(action) ||
        ledger?.ledgerOwned !== true
    ) {
        return action;
    }
    const { [termId]: cell, ...otherCells } = responseTerms.byId;
    if (!cell || !isPricedOnSupersededLedger(cell, ledger, requestLedgerRebates)) {
        return action;
    }
    return { ...action, payload: { ...action.payload, terms: { ...responseTerms, byId: otherCells } } };
};

const mergeAndReconcileTerms = (
    terms: Terms,
    action: AnyAction,
    penciledInputs: PenciledInputsState,
    offerType: OfferType,
    restorationEnabled: boolean,
    legacyMerge: (prevTerms: Terms, merged: MergedTerms) => Terms,
    currentTermId: string | undefined,
    upqProgramLossBypass: boolean,
    appliedIncentiveDisplayFixesEnabled: boolean,
    responseAppliedIncentiveAmountEnabled: boolean,
    gridIncentiveManagementEnabled = false
): Terms => {
    const merged = mergeTerms(terms, action.payload?.terms);
    // DE760840 (AC3): the applied set is otherwise taken straight from the response, so a call requested for
    // a term the user has since left can clear the incentive applied to the current term.
    const guard = (result: Terms) =>
        preserveSelectedTermAppliedIncentives(terms, result, action, penciledInputs, offerType, currentTermId);
    if (!restorationEnabled) {
        return guard(legacyMerge(terms, merged));
    }
    // Not guarded: isVehicleSwitchRecalc requires originalAction SWITCH_VEHICLE and supersession requires
    // SELECTED_TERM, so the two are mutually exclusive and the guard could only ever be a no-op here.
    if (isVehicleSwitchRecalc(action)) {
        return dropStandardsTheResponseDidNotApply(
            syncVehicleSwitchTermIncentives(merged, action, penciledInputs, offerType),
            action,
            offerType
        );
    }
    const incentivesRequested =
        action.meta?.includeIncentives !== false ||
        (isIncentiveDrivenRecalc(action) && responseTermHasAppliedIncentiveIds(action, penciledInputs, offerType));
    const reconciled = reconcileMergedTermIncentives(
        terms,
        merged,
        penciledInputs,
        offerType,
        incentivesRequested,
        !isProgramLookupOutcome(action),
        didServiceSeedStandards(action),
        upqProgramLossBypass,
        appliedIncentiveDisplayFixesEnabled,
        responseAppliedIncentiveAmountEnabled
    );
    const adopted =
        gridIncentiveManagementEnabled && isRegularGridRowResponse(action)
            ? adoptGridResponseTermIncentives(reconciled, action, currentTermId)
            : reconciled;
    // The guard runs before the seed and drop steps, not after: those legitimately refine the applied set
    // from penciled inputs, so restoring the pre-response value afterwards would undo the seed and could
    // resurrect a conditional the drops had just removed.
    const guarded = guard(adopted);
    const seeded = seedSelectedTermAppliedIncentivesFromPenciled(guarded, penciledInputs, offerType);
    const compatible = dropNonCompatibleCatalogIncentives(seeded, penciledInputs, offerType);
    const pruned = dropExclusiveConditionalIncentives(
        dropStandardsTheResponseDidNotApply(compatible, action, offerType),
        penciledInputs,
        offerType
    );
    return gridIncentiveManagementEnabled
        ? adoptSelectedTermResponseIncentives(
              pruned,
              action,
              penciledInputs,
              offerType,
              getOfferTypeTermId(penciledInputs, offerType)
          )
        : pruned;
};

const buildCompatibleConditionalKeys = (
    offerType: OfferType,
    catalogConditionals: Array<string | number>,
    compatible: number[]
): IPencilOfferInputsIncentive[] => {
    const compatibleSet = new Set<number>(compatible);
    return catalogConditionals
        .filter((id) => compatibleSet.has(typeof id === 'number' ? id : parseIncentiveId(String(id))))
        .map((id) => ({
            id: typeof id === 'number' ? buildConditionalIncentiveKey(offerType, id) : id
        })) as IPencilOfferInputsIncentive[];
};

const mergeCompatibleConditionalsIntoTerm = (
    term: GenericObject,
    offerType: OfferType,
    catalogConditionals: Array<string | number>
): GenericObject | null => {
    const compatible = getTermCompatibleIds(term);
    if (compatible.length === 0 || catalogConditionals.length === 0) {
        return null;
    }
    const compatibleConditionals = buildCompatibleConditionalKeys(offerType, catalogConditionals, compatible);
    if (compatibleConditionals.length === 0) {
        return null;
    }
    const existing = (term.incentives?.conditionals ?? []) as IPencilOfferInputsIncentive[];
    const existingIdSet = new Set(existing.map((incentive) => parseIncentiveId(String(incentive.id))));
    const missing = compatibleConditionals.filter((incentive) => !existingIdSet.has(parseIncentiveId(String(incentive.id))));
    if (missing.length === 0) {
        return null;
    }
    return {
        ...term,
        incentives: {
            ...term.incentives,
            conditionals: [...existing, ...missing],
            nonConditionals: term.incentives?.nonConditionals ?? []
        }
    };
};

export const seedEmptyTermConditionalsFromCatalog = (
    terms: Terms,
    incentives: { byOfferType?: Record<string, { conditionals?: Array<string | number> }> }
): Terms => {
    const byOfferTypeCatalog = incentives?.byOfferType;
    const byId = terms.byId;
    if (!byOfferTypeCatalog || !byId) {
        return terms;
    }
    let changed = false;
    const nextById: GenericObject = { ...byId };
    Object.keys(byId).forEach((termId) => {
        const term = byId[termId] as GenericObject;
        const offerType = termId.split('_')[0] as OfferType;
        if (offerType === CASH) {
            return;
        }
        const catalogConditionals = byOfferTypeCatalog[offerType]?.conditionals ?? [];
        const merged = mergeCompatibleConditionalsIntoTerm(term, offerType, catalogConditionals);
        if (merged) {
            nextById[termId] = merged;
            changed = true;
        }
    });
    return changed ? ({ ...terms, byId: nextById } as Terms) : terms;
};

const getPenciledTermId = (penciledInputs: PenciledInputsState, offerType: OfferType): string | undefined =>
    (penciledInputs.byOfferType?.[offerType] as GenericObject | undefined)?.term as string | undefined;

const getLoadedOfferPayment = (action: AnyAction): GenericObject | undefined =>
    (action.type === types.SYNC_MESSAGE ? action.message.offer.payment : action.payload.payment) as GenericObject | undefined;

const hydrateSelectedTermProgramFromPayment = (
    terms: Terms,
    action: AnyAction,
    offerType: OfferType,
    penciledInputs: PenciledInputsState,
    programIncentivesRestorationEnabled: boolean
): Terms => {
    if (!programIncentivesRestorationEnabled || offerType === CASH) {
        return terms;
    }
    const payment = getLoadedOfferPayment(action);
    if (!payment?.lenderProgram?.programName) {
        return terms;
    }
    const termId = getPenciledTermId(penciledInputs, offerType);
    if (termId == null) {
        return terms;
    }
    const term = terms.byId?.[termId] as GenericObject | undefined;
    if (term == null || term.lenderProgram?.programName) {
        return terms;
    }
    return {
        ...terms,
        byId: {
            ...terms.byId,
            [termId]: {
                ...term,
                lenderProgram: payment.lenderProgram,
                lenderLegalName: payment.lenderLegalName ?? term.lenderLegalName,
                ...fillMissingCreditTier(term, payment)
            }
        }
    } as Terms;
};

/**
 * Explicit, so an offer type with no grid list fails loudly instead of being mis-filed.
 * An `else` default sent CASH — and anything added later — into `financeTerms`, polluting the
 * list `getOfferTypeTerms` hands to the grid and to the payment payloads.
 */
const GRID_TERM_LIST_KEY_BY_OFFER_TYPE: Partial<Record<OfferType, 'financeTerms' | 'leaseTerms' | 'balloonTerms'>> = {
    [FINANCE]: 'financeTerms',
    [LEASE]: 'leaseTerms',
    [BALLOON]: 'balloonTerms'
};

const withoutTermRecords = (terms: Terms, offerType: OfferType, termMonths: number[]): Terms => {
    const listKey = GRID_TERM_LIST_KEY_BY_OFFER_TYPE[offerType];
    if (!listKey) return terms;
    const existingList = ((terms as GenericObject)[listKey] as string[]) ?? [];
    const termsById = terms.byId ?? {};
    const staleIds = new Set(existingList.filter((id) => termMonths.includes(termsById[id]?.months)));
    if (!staleIds.size) return terms;

    const byId = Object.fromEntries(Object.entries(termsById).filter(([id]) => !staleIds.has(id)));
    return {
        ...terms,
        byId,
        allIds: (terms.allIds ?? []).filter((id) => !staleIds.has(id)),
        [listKey]: existingList.filter((id) => !staleIds.has(id))
    } as Terms;
};

export const buildUpdatedTermsState = (
    terms: Terms = initialState.terms,
    action: AnyAction,
    offerType: OfferType,
    penciledInputs: PenciledInputsState,
    programIncentivesRestorationEnabled = false,
    currentTermId?: string,
    gridCashDownCatalogCarryEnabled = false,
    upqProgramLossBypass = false,
    appliedIncentiveDisplayFixesEnabled = false,
    responseAppliedIncentiveAmountEnabled = false,
    gridIncentiveManagementEnabled = false,
    gridRebateIdsConsistencyEnabled = false
): Terms => {
    switch (action.type) {
        case types.ADOPT_PAYMENT_GRID_CELL: {
            const { termId, cell } = action.payload ?? {};
            if (!termId || !cell) return terms;
            const adopted = { ...terms.byId?.[termId], ...cell };
            return {
                ...terms,
                byId: {
                    ...terms.byId,
                    [termId]: gridIncentiveManagementEnabled ? keepRecordIncentiveList(terms.byId?.[termId], adopted) : adopted
                }
            } as Terms;
        }
        case types.INIT_OFFER: {
            const hydrated = { ...terms, ...action.payload.terms };
            const migrated = migrateLegacyAppliedIncentiveIds(hydrated, programIncentivesRestorationEnabled);
            return hydrateSelectedTermProgramFromPayment(
                migrated,
                action,
                offerType,
                penciledInputs,
                programIncentivesRestorationEnabled
            );
        }
        case types.INIT_SUCCESS: {
            if (offerType === CASH) {
                return terms;
            }
            const payload = action.payload;
            const termId = getOfferTypeTermId(penciledInputs, offerType);
            const { capReductionTaxCapitalizedIndicator } = payload.calculationDetails?.payment || {};

            if (capReductionTaxCapitalizedIndicator == null || termId == null) {
                return terms;
            }

            return {
                ...terms,
                byId: {
                    ...terms.byId,
                    [termId]: {
                        ...terms.byId![termId],
                        capReductionTaxCapitalizedIndicator
                    }
                }
            };
        }

        // Checking if offer type is cash TODO: Fix when payment driver is fixed
        case types.PAYMENT_RECEIVED: {
            if (action.meta?.isStalePaymentResponse) {
                return terms;
            }
            if (offerType !== CASH) {
                const received = gridRebateIdsConsistencyEnabled
                    ? withoutStaleSelectedGridCell(action, penciledInputs, offerType, currentTermId)
                    : action;
                return keepLockedProgramOnUpqTerms(
                    mergeAndReconcileTerms(
                        terms,
                        received,
                        penciledInputs,
                        offerType,
                        programIncentivesRestorationEnabled,
                        (prev, merged) => preserveTermIncentives(prev, merged, false),
                        currentTermId,
                        upqProgramLossBypass,
                        appliedIncentiveDisplayFixesEnabled,
                        responseAppliedIncentiveAmountEnabled,
                        gridIncentiveManagementEnabled
                    ),
                    action
                );
            }
            return terms;
        }
        /**
         * US2085313: merges a stored grid's cells back into the terms node on load.
         *
         * A merge, never a replace. `INIT_OFFER` has already hydrated the deal's active term, and that is
         * the record the deal screen is reading — restoring must widen the node around it, not rebuild it.
         * The ids also join the offer type's own term list, because `getOfferTypeTerms` is what the grid
         * and the payment payloads enumerate; a record in `byId` that no list mentions is invisible to both.
         */
        case types.RESTORE_PAYMENT_GRID_TERMS: {
            const { offerType: restoredOfferType, byId } = action.payload;
            const restoredIds = Object.keys(byId);
            if (!restoredIds.length) return terms;

            const listKey = GRID_TERM_LIST_KEY_BY_OFFER_TYPE[restoredOfferType as OfferType];
            if (!listKey) return terms;
            const existingList = ((terms as GenericObject)[listKey] as string[]) ?? [];

            return {
                ...terms,
                // The stored record loses to anything already in state: a live response is never older.
                byId: { ...byId, ...terms.byId },
                allIds: Array.from(new Set([...(terms.allIds ?? []), ...restoredIds])),
                [listKey]: Array.from(new Set([...existingList, ...restoredIds]))
            } as Terms;
        }
        case types.CLEAR_PAYMENT_GRID_TERM_RECORDS: {
            const { offerType: clearedOfferType, termMonths } = action.payload;
            return withoutTermRecords(terms, clearedOfferType, [termMonths]);
        }
        case types.RESET_PAYMENT_GRID: {
            const { offerType: resetOfferType, staleQuoteTerms } = action.payload;
            return withoutTermRecords(terms, resetOfferType, staleQuoteTerms ?? []);
        }
        case types.LOCATION_STANDARDS_RECONCILED:
        case types.UPDATE_SELECTED_INCENTIVES: {
            if (!programIncentivesRestorationEnabled || offerType === CASH || gridIncentiveManagementEnabled) {
                return terms;
            }
            return syncSelectedTermAppliedIncentives(terms, penciledInputs, offerType, true);
        }
        case types.SELECTED_TERM:
        case types.SELECTED_CREDIT_TIER: {
            if (!programIncentivesRestorationEnabled || offerType === CASH) {
                return terms;
            }
            return carryAppliedIncentivesToSelectedTerm(
                terms,
                offerType,
                getOfferTypeTermId(penciledInputs, offerType),
                gridCashDownCatalogCarryEnabled,
                gridIncentiveManagementEnabled
            );
        }
        // Checking if offer type is cash TODO: Fix when payment driver is fixed (copied from Alex Rogers comment 9 months back)
        case types.SYNC_MESSAGE: {
            const hydrated = { ...terms, ...action.message.offer.terms };
            const migrated = migrateLegacyAppliedIncentiveIds(hydrated, programIncentivesRestorationEnabled);
            return hydrateSelectedTermProgramFromPayment(
                migrated,
                action,
                offerType,
                penciledInputs,
                programIncentivesRestorationEnabled
            );
        }
        case types.PAYMENT_FAILURE: {
            if (action.meta?.shouldKeepPaymentsOnFailure) {
                return terms;
            }
            if (offerType !== CASH && !shouldRevertPaymentOnFailure(terms, action, offerType)) {
                return mergeAndReconcileTerms(
                    terms,
                    action,
                    penciledInputs,
                    offerType,
                    programIncentivesRestorationEnabled,
                    (_prev, merged) => merged,
                    currentTermId,
                    false,
                    appliedIncentiveDisplayFixesEnabled,
                    responseAppliedIncentiveAmountEnabled
                );
            }
            return terms;
        }
        case types.UPDATE_AVAILABLE_INCENTIVES: {
            const withDetails = programIncentivesRestorationEnabled;
            const financeIncentives = action.payload?.finance?.incentives || {};
            const leaseIncentives = action.payload?.lease?.incentives || {};
            const financeTermIncentives: {
                conditionals: IPencilOfferInputsIncentive[];
                nonConditionals: IPencilOfferInputsIncentive[];
            } = {
                conditionals: [],
                nonConditionals: []
            };
            const leaseTermIncentives: {
                conditionals: IPencilOfferInputsIncentive[];
                nonConditionals: IPencilOfferInputsIncentive[];
            } = {
                conditionals: [],
                nonConditionals: []
            };

            financeTermIncentives.conditionals = updateTermIncentives(
                financeIncentives.conditionals,
                'conditional_finance_',
                withDetails,
                withDetails
            );
            financeTermIncentives.nonConditionals = updateTermIncentives(
                financeIncentives.nonConditionals,
                'nonconditional_finance_',
                withDetails,
                withDetails
            );
            leaseTermIncentives.conditionals = updateTermIncentives(
                leaseIncentives.conditionals,
                'conditional_lease_',
                withDetails,
                withDetails
            );
            leaseTermIncentives.nonConditionals = updateTermIncentives(
                leaseIncentives.nonConditionals,
                'nonconditional_lease_',
                withDetails,
                withDetails
            );

            const applyCatalog = (term: GenericObject, catalog: GenericObject): GenericObject =>
                withDetails && hasIncentiveCatalog(term) ? term : { ...term, incentives: catalog };

            const updatedById = Object.keys(terms.byId!).reduce((acc: GenericObject, termName) => {
                if (termName.startsWith('finance_')) {
                    acc[termName] = applyCatalog(terms.byId![termName], financeTermIncentives);
                } else if (termName.startsWith('lease_')) {
                    acc[termName] = applyCatalog(terms.byId![termName], leaseTermIncentives);
                } else {
                    acc[termName] = terms.byId![termName];
                }
                return acc;
            }, {});

            return {
                ...terms,
                byId: updatedById
            };
        }
        case types.UPDATE_TERM_AVAILABLE_INCENTIVES: {
            if (offerType === CASH) {
                return terms;
            }
            const { conditionals, nonConditionals, incentivesContext, standardsEchoIsAuthoritative } = action.payload ?? {};
            const termIncentives = {
                conditionals: updateTermIncentives(
                    conditionals,
                    `conditional_${offerType}_`,
                    true,
                    programIncentivesRestorationEnabled
                ),
                nonConditionals: updateTermIncentives(
                    nonConditionals,
                    `nonconditional_${offerType}_`,
                    true,
                    programIncentivesRestorationEnabled
                )
            };

            const selectedTermId = getOfferTypeTermId(penciledInputs, offerType);
            const selectedTermData = selectedTermId != null ? terms.byId?.[selectedTermId] : undefined;
            if (selectedTermId == null || selectedTermData == null) {
                return terms;
            }
            const incomingCatalogEmpty = termIncentives.conditionals.length === 0 && termIncentives.nonConditionals.length === 0;
            const existingCatalog = (selectedTermData as { incentives?: { conditionals?: unknown[]; nonConditionals?: unknown[] } })
                .incentives;
            const existingCatalogNonEmpty =
                (existingCatalog?.conditionals?.length ?? 0) + (existingCatalog?.nonConditionals?.length ?? 0) > 0;
            const toContextSignature = (context?: { programContextId?: string; incentivesInputsKey?: string }): string | null =>
                context == null ? null : JSON.stringify([context.programContextId, context.incentivesInputsKey]);
            const existingContextSignature = toContextSignature(
                (selectedTermData as { incentivesContext?: { programContextId?: string; incentivesInputsKey?: string } })
                    .incentivesContext
            );
            const isDifferentKnownContext =
                existingContextSignature !== null && existingContextSignature !== toContextSignature(incentivesContext);
            const isSameKnownContext =
                existingContextSignature !== null && existingContextSignature === toContextSignature(incentivesContext);
            const preserveExistingCatalog = incomingCatalogEmpty && existingCatalogNonEmpty && !isDifferentKnownContext;
            const termsWithCatalog = {
                ...terms,
                byId: {
                    ...terms.byId,
                    [selectedTermId]: {
                        ...selectedTermData,
                        ...(preserveExistingCatalog ? {} : { incentives: termIncentives }),
                        ...(incentivesContext != null && { incentivesContext })
                    }
                }
            } as Terms;
            const existingApplied = (selectedTermData as GenericObject).appliedIncentiveIds as number[] | undefined;
            const preserveApplied =
                Array.isArray(existingApplied) && (existingApplied.length > 0 || isSameKnownContext) && !isDifferentKnownContext;
            const effectiveCatalog = (preserveExistingCatalog ? existingCatalog : termIncentives) as
                | { conditionals?: unknown[]; nonConditionals?: unknown[] }
                | undefined;
            const catalogNonEmpty =
                (effectiveCatalog?.conditionals?.length ?? 0) + (effectiveCatalog?.nonConditionals?.length ?? 0) > 0;
            const dropOrphans = preserveApplied && catalogNonEmpty;
            if (programIncentivesRestorationEnabled && gridIncentiveManagementEnabled && preserveApplied) {
                return termsWithCatalog;
            }
            return programIncentivesRestorationEnabled
                ? dropExclusiveConditionalIncentives(
                      dropNonCompatibleCatalogIncentives(
                          syncSelectedTermAppliedIncentives(
                              termsWithCatalog,
                              penciledInputs,
                              offerType,
                              preserveApplied,
                              dropOrphans,
                              preserveApplied,
                              Boolean(standardsEchoIsAuthoritative)
                          ),
                          penciledInputs,
                          offerType
                      ),
                      penciledInputs,
                      offerType
                  )
                : termsWithCatalog;
        }
        case types.TOGGLE_RATE_TYPE: {
            const { selectedTerm, rateType } = action.payload;
            const termsByIds = terms.byId;
            const selectedTermData = termsByIds![selectedTerm];
            if (!selectedTermData) {
                return terms;
            }

            // get offer type prefix so we convert based on the correct financeMethod.
            // Eg: if the term is finance_36 then we know the offer type is finance and we convert the rates based on that
            const prefix = selectedTerm.split('_')[0] + '_'; // grabs "finance_" from [finance_36, finance_72] etc...

            const updatedById = Object.fromEntries(
                Object.entries(termsByIds!).map(([termId, termData]: [string, any]) => {
                    if (!termId.startsWith(prefix)) {
                        return [termId, termData];
                    }
                    const { transformedBuyRate, transformedSellRate } = convertRatesFromSourceType(
                        rateType,
                        termData.rateType,
                        termData.buyRate ?? null,
                        termData.sellRate ?? null
                    );
                    return [
                        termId,
                        {
                            ...termData,
                            rateType,
                            ...(transformedBuyRate !== undefined && { buyRate: transformedBuyRate }),
                            ...(transformedSellRate !== undefined && { sellRate: transformedSellRate })
                        }
                    ];
                })
            );

            return {
                ...terms,
                byId: updatedById
            };
        }
        case types.UPDATE_PROFITABILITY_DATA: {
            const { offerType: actionOfferType, termId, profitabilityData } = action.payload;

            if (actionOfferType === CASH) {
                return terms;
            }

            if (!termId || !terms.byId![termId]) {
                return terms;
            }

            return {
                ...terms,
                byId: {
                    ...terms.byId,
                    [termId]: {
                        ...terms.byId![termId],
                        ...profitabilityData
                    }
                }
            };
        }
        default: {
            return terms;
        }
    }
};
